import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import type { UserCredential } from 'firebase/auth';
import ENV from 'frontend-login/config/environment';
import type RegisterController from 'frontend-login/controllers/register';
import fetch, { NetworkError } from 'frontend-login/utils/fetch';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';

interface QueryParams {
  isRedirecting?: boolean;
  next?: string;
  token?: string;
  uidb64?: string;
}

type Model =
  | undefined
  | {
      email: string;
      ssoEnforced: boolean;
    };

export const TOKEN_EXPIRED_ERROR_CAUSE = 'token_expired';

export class TokenExpiredError extends Error {
  cause = TOKEN_EXPIRED_ERROR_CAUSE;
  name = 'TokenExpiredError';
}

export default class RegisterRoute extends Route {
  queryParams = {
    next: {
      refreshModel: false,
    },
    isRedirecting: {
      refreshModel: false,
    },
    isHandlingRedirect: {
      refreshModel: false,
    },
    token: {
      refreshModel: false,
    },
    uidb64: {
      refreshModel: false,
    },
  };

  @service declare authentication: Services['authentication'];
  @service declare sharedAuth: Services['shared-auth'];

  async model(params: QueryParams): Promise<Model> {
    try {
      const host = getHostDomain({ frontendGateway: ENV.frontendGateway });
      await fetch(`${host}/api/v3/users/?only_self=true`);
      this.redirectToRoot();
      return;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_: unknown) {
      // Do nothing and allow registration to continue
    }

    const url = new URL(
      '/apigateway/v1/app/password-token-info/',
      getHostDomain({ frontendGateway: ENV.frontendGateway }),
    );
    url.searchParams.append('token', params.token!);
    url.searchParams.append('uidb64', params.uidb64!);

    try {
      return (await fetch(url.toString())) as Model;
    } catch (error: unknown) {
      if (!(error instanceof NetworkError)) {
        throw error;
      }

      type Error = {
        code: string;
        title: string;
      };

      const data = (await error.cause.response.json()) as { errors: Error[] };
      const firstError = data.errors[0]!;

      if (firstError.code === TOKEN_EXPIRED_ERROR_CAUSE) {
        throw new TokenExpiredError();
      }

      throw error;
    }
  }

  redirectToRoot(): void {
    window.location.replace('/');
  }

  setupController(
    controller: RegisterController,
    model: Model,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    this.authentication.handleRedirect().then(
      (result: UserCredential | null) =>
        this.sharedAuth.handleRedirectLogic(result, null),
      (error: unknown) => this.sharedAuth.handleRedirectLogic(null, error),
    );

    const queryParams: QueryParams = this.paramsFor(this.routeName);

    const email = model?.email;
    if (email && !controller.isHandlingRedirect) {
      controller.isHandlingRedirect = true;
      this.sharedAuth.emailInputValue = email;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.sharedAuth.fetchLoginOptions(email);
    } else {
      controller.isHandlingRedirect = false;
    }

    if (queryParams.isRedirecting) {
      this.sharedAuth.isRedirecting = true;
    }
  }
}
